import type { FormEvent, ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SubscriptionStatus } from '@cookspace-shared-contracts/core';
import { Eye } from 'lucide-react';

import {
    useCancelSubscriptionMutation,
    useListSubscriptionsQuery
} from '@/api/subscriptionsApi';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import type { AdminSubscriptionListItem } from '@/lib/subscriptionsSchemas';

const REASON_MIN_LENGTH = 10;

const STATUS_OPTIONS: Array<{ value: SubscriptionStatus; label: string }> = [
    { value: 'pending', label: 'Ожидает оплаты' },
    { value: 'active', label: 'Активна' },
    { value: 'grace_period', label: 'Grace period' },
    { value: 'expired', label: 'Истекла' },
    { value: 'cancelled', label: 'Отменена' }
];

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
    pending: 'Ожидает',
    active: 'Активна',
    grace_period: 'Grace',
    expired: 'Истекла',
    cancelled: 'Отменена'
};

const STATUS_COLOR: Record<SubscriptionStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    grace_period: 'bg-orange-100 text-orange-800',
    expired: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-red-100 text-red-800'
};

const PAGE_SIZE = 20;

function formatDate(iso: string | null): string {
    if (iso === null) {
        return '—';
    }
    return new Date(iso).toLocaleDateString('ru-RU');
}

function formatUserContact(sub: AdminSubscriptionListItem): string {
    const { user } = sub;
    if (user === null) {
        return sub.userId.slice(0, 8);
    }
    if (user.email !== null && user.email !== '') {
        return user.email;
    }
    if (user.phoneNumber !== null && user.phoneNumber !== '') {
        return user.phoneNumber;
    }
    return user.displayName ?? user.id.slice(0, 8);
}

interface CancelIntent {
    sub: AdminSubscriptionListItem;
}

export function SubscriptionsPage(): ReactElement {
    const navigate = useNavigate();

    const [filterStatus, setFilterStatus] = useState<SubscriptionStatus | ''>('');
    const [searchInput, setSearchInput] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(0);

    const queryArg = useMemo(
        () => ({
            ...(filterStatus ? { status: filterStatus } : {}),
            ...(searchValue !== '' ? { search: searchValue } : {}),
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE
        }),
        [filterStatus, searchValue, page]
    );

    const { data, isLoading, isError } = useListSubscriptionsQuery(queryArg);
    const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();

    const [cancelIntent, setCancelIntent] = useState<CancelIntent | null>(null);
    const [cancelImmediate, setCancelImmediate] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelReasonError, setCancelReasonError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE);

    const handleSearchSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault();
        setSearchValue(searchInput.trim());
        setPage(0);
    };

    const openCancel = (sub: AdminSubscriptionListItem): void => {
        setCancelIntent({ sub });
        setCancelImmediate(false);
        setCancelReason('');
        setCancelReasonError(null);
    };

    const closeCancel = (): void => {
        setCancelIntent(null);
        setCancelImmediate(false);
        setCancelReason('');
        setCancelReasonError(null);
    };

    const submitCancel = async (): Promise<void> => {
        if (!cancelIntent) {
            return;
        }

        const reasonTrimmed = cancelReason.trim();
        if (reasonTrimmed.length < REASON_MIN_LENGTH) {
            setCancelReasonError(`Минимум ${REASON_MIN_LENGTH} символов`);
            return;
        }
        setCancelReasonError(null);

        try {
            await cancelSubscription({
                id: cancelIntent.sub.id,
                body: { reason: reasonTrimmed, immediate: cancelImmediate }
            }).unwrap();
            closeCancel();
        } catch {
            setPageError('Не удалось отменить подписку');
            closeCancel();
        }
    };

    return (
        <div data-testid="subscriptions-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Подписки</h1>
            </div>

            <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3">
                <div className="w-56">
                    <Select
                        label="Статус"
                        value={filterStatus}
                        onChange={(e) => {
                            setFilterStatus(e.target.value as SubscriptionStatus | '');
                            setPage(0);
                        }}
                        placeholder="Все статусы"
                        options={STATUS_OPTIONS}
                        data-testid="subscriptions-filter-status"
                    />
                </div>
                <form
                    onSubmit={handleSearchSubmit}
                    className="flex w-72 items-end gap-2"
                    data-testid="subscriptions-search-form"
                >
                    <div className="flex-1">
                        <Input
                            label="Поиск (email/телефон)"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="user@mail.ru"
                            data-testid="subscriptions-search-input"
                        />
                    </div>
                    <button
                        type="submit"
                        className="rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                        data-testid="subscriptions-search-submit"
                    >
                        Найти
                    </button>
                </form>
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="subscriptions-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="subscriptions-error">
                    Не удалось загрузить подписки
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="subscriptions-table"
                >
                    <div className="grid grid-cols-[80px_minmax(0,1.4fr)_minmax(0,1fr)_120px_120px_100px_140px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Пользователь</span>
                        <span>Тариф</span>
                        <span>Статус</span>
                        <span>До</span>
                        <span>Отмена</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Подписки не найдены
                        </div>
                    )}

                    {items.map((sub) => (
                        <div
                            key={sub.id}
                            className="grid grid-cols-[80px_minmax(0,1.4fr)_minmax(0,1fr)_120px_120px_100px_140px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`subscription-row-${sub.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {sub.id.slice(0, 8)}
                            </span>
                            <span className="truncate text-gray-900">
                                {formatUserContact(sub)}
                            </span>
                            <span className="truncate text-gray-700">{sub.tariff?.name ?? '—'}</span>
                            <span>
                                <span
                                    className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLOR[sub.status]}`}
                                >
                                    {STATUS_LABEL[sub.status]}
                                </span>
                            </span>
                            <span className="text-xs text-gray-700">
                                {formatDate(sub.currentPeriodEnd)}
                            </span>
                            <span className="text-xs text-gray-700">
                                {sub.cancelAtPeriodEnd ? 'Да' : '—'}
                            </span>
                            <span className="flex justify-end gap-1">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/subscriptions/${sub.id}`)}
                                    data-testid={`subscription-view-${sub.id}`}
                                    className="rounded p-1.5 text-gray-600 transition hover:bg-gray-100"
                                    aria-label="Просмотр"
                                    title="Просмотр"
                                >
                                    <Eye size={14} />
                                </button>
                                {(sub.status === 'active' ||
                                    sub.status === 'grace_period' ||
                                    sub.status === 'pending') && (
                                    <button
                                        type="button"
                                        onClick={() => openCancel(sub)}
                                        data-testid={`subscription-cancel-${sub.id}`}
                                        className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 transition hover:bg-red-50"
                                    >
                                        Отменить
                                    </button>
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div
                    className="flex items-center justify-between text-sm text-gray-700"
                    data-testid="subscriptions-pagination"
                >
                    <span>
                        Страница {page + 1} из {totalPages} · всего {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="subscriptions-page-prev"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Назад
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page + 1 >= totalPages}
                            data-testid="subscriptions-page-next"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Вперёд
                        </button>
                    </div>
                </div>
            )}

            <Modal
                isOpen={cancelIntent !== null}
                onClose={closeCancel}
                title="Отмена подписки"
                testId="subscription-cancel-modal"
            >
                {cancelIntent !== null && (
                    <div className="space-y-4" data-testid="subscription-cancel-form">
                        <p className="text-sm text-gray-700">
                            Подписка пользователя{' '}
                            <span className="font-medium">
                                {formatUserContact(cancelIntent.sub)}
                            </span>{' '}
                            на тариф{' '}
                            <span className="font-medium">{cancelIntent.sub.tariff?.name ?? '—'}</span>.
                        </p>

                        <div className="space-y-2 rounded border border-gray-200 p-3">
                            <label className="flex items-start gap-3 text-sm">
                                <input
                                    type="radio"
                                    name="cancelMode"
                                    checked={!cancelImmediate}
                                    onChange={() => setCancelImmediate(false)}
                                    data-testid="subscription-cancel-period-end"
                                    className="mt-0.5"
                                />
                                <span>
                                    <span className="font-medium text-gray-900">
                                        В конце оплаченного периода
                                    </span>
                                    <span className="block text-xs text-gray-600">
                                        Доступ сохранится до{' '}
                                        {formatDate(cancelIntent.sub.currentPeriodEnd)}
                                    </span>
                                </span>
                            </label>
                            <label className="flex items-start gap-3 text-sm">
                                <input
                                    type="radio"
                                    name="cancelMode"
                                    checked={cancelImmediate}
                                    onChange={() => setCancelImmediate(true)}
                                    data-testid="subscription-cancel-immediate"
                                    className="mt-0.5"
                                />
                                <span>
                                    <span className="font-medium text-gray-900">Немедленно</span>
                                    <span className="block text-xs text-gray-600">
                                        Доступ закроется сразу. Возврат денег не оформляется.
                                    </span>
                                </span>
                            </label>
                        </div>

                        <Textarea
                            label={`Причина отмены (минимум ${REASON_MIN_LENGTH} символов)`}
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            rows={4}
                            error={cancelReasonError ?? undefined}
                            data-testid="subscription-cancel-reason"
                        />

                        <div className="flex justify-end gap-2 pt-2">
                            <button
                                type="button"
                                onClick={closeCancel}
                                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                            >
                                Отмена
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    void submitCancel();
                                }}
                                disabled={isCancelling}
                                data-testid="subscription-cancel-submit"
                                className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                            >
                                Отменить подписку
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
